// `Fragment as Rows`: each library entry renders TWO <tr>s (the row and its
// expanded note) and a bare <>…</> cannot carry a key, so React would list
// them unkeyed. Aliased because `Fragment` is already the name of the thing
// being rendered — a copy fragment — and two meanings for one word in one file
// is how the wrong one gets imported later.
import { Fragment as Rows, useEffect, useMemo, useState } from 'react'
import { api, type Fragment, type LibraryPayload, type SegmentCoverage } from '../api'
import { useRefreshOnActivate } from '../useActive'

/**
 * Library — every predefined sentence, and the data it draws on.
 *
 * `sections.yaml` has said since v1 that a fragment's status is "draft |
 * pending | approved. Only `approved` composes; the others appear in the
 * Library screen so the gate is visible." Nothing built that screen until now, so for
 * the whole rewrite the gate has been invisible: the only way to find out that
 * a segment had six fragments nobody had read was to open the YAML. A gate
 * nobody can see is one that gets discovered at send time.
 *
 * TWO THINGS DECIDE WHAT AN ACCOUNT RECEIVES, and both are invisible in the
 * file, so both are on the screen:
 *
 *   POSITION. Order within a slot is specificity and first match wins. A
 *   fragment below the unconditional fallback can never be selected, and looks
 *   identical in the file to one that always is. Rows are listed in
 *   composition order with the index shown, and an unreachable one is called
 *   out rather than left to be inferred.
 *
 *   DATA. A fragment naming {{cycle_visits}} is a sentence about an account's
 *   own figures — and it is SKIPPED for any account where that value is empty,
 *   which drops the reader to whatever sits below. So "which sentences are
 *   data-driven" and "which sentences can fall through" are one question, and
 *   the field chips answer it.
 *
 * Read-only, deliberately. Approving a fragment is an edit to the file next to
 * the note recording who asked for it; a button here would separate the two,
 * and the note is the part that stops the same argument being had twice.
 */

const STATUS_BADGE: Record<string, string> = {
  approved: 'ok',
  pending: 'parked',
  draft: 'catchall',
}

export function LibraryTab({ active = true }: { active?: boolean }) {
  const [data, setData] = useState<LibraryPayload | null>(null)
  const [coverage, setCoverage] = useState<SegmentCoverage[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [slot, setSlot] = useState<string>('')
  const [status, setStatus] = useState<string>('')
  const [segment, setSegment] = useState<string>('')
  const [field, setField] = useState<string>('')
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState<string | null>(null)

  const load = async () => {
    try {
      setError(null)
      setData(await api.library())
    } catch (err) {
      setError(String((err as Error).message))
    }
    // Coverage needs the database and the rest does not, so a filters table
    // that will not answer costs the segment panel and nothing else.
    try {
      setCoverage((await api.libraryCoverage()).segments)
    } catch {
      setCoverage([])
    }
  }

  useEffect(() => {
    void load()
  }, [])
  useRefreshOnActivate(active, load)

  const segments = useMemo(() => {
    const all = new Set<string>()
    for (const f of data?.fragments ?? []) for (const g of f.segments) all.add(g)
    return [...all].sort()
  }, [data])

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return (data?.fragments ?? []).filter((f) => {
      if (slot && f.slot !== slot) return false
      if (status && f.status !== status) return false
      if (segment && !f.segments.includes(segment)) return false
      if (field && !f.uses.includes(field)) return false
      if (needle) {
        const hay = `${f.id} ${f.text} ${f.when ?? ''}`.toLowerCase()
        if (!hay.includes(needle)) return false
      }
      return true
    })
  }, [data, slot, status, segment, field, search])

  if (error) return <div className="error">{error}</div>
  if (!data) return <div className="loading">Loading the library…</div>

  const { summary } = data
  const bySlot = new Map<string, Fragment[]>()
  for (const f of shown) bySlot.set(f.slot, [...(bySlot.get(f.slot) ?? []), f])

  const filtered = shown.length !== summary.total

  return (
    <>
      <div className="panel">
        <h2>
          Copy library <span className="count">{summary.total}</span>
        </h2>
        <div className="panel-body">
          <p className="hint">{data.note}</p>
          <div className="row">
            <span className="badge ok">{summary.by_status.approved ?? 0} approved</span>
            <span className="badge parked">{summary.by_status.pending ?? 0} pending</span>
            <span className="badge catchall">{summary.by_status.draft ?? 0} draft</span>
            <span className="hint">
              Only approved copy composes. Pending means finished copy waiting on a person to
              read it; draft means somebody is still writing it — a queue and a notepad, not
              two names for the same thing.
            </span>
          </div>

          {summary.unreachable.length > 0 && (
            <div className="error">
              Never selected, because they sit below the catch-all in their slot:{' '}
              <span className="mono">{summary.unreachable.join(', ')}</span>
            </div>
          )}
          {summary.unknown_fields.length > 0 && (
            <div className="error">
              Names a merge field the engine cannot fill, so it would render the placeholder
              literally: <span className="mono">{summary.unknown_fields.join(', ')}</span>
            </div>
          )}
          {summary.undeclared.length > 0 && (
            <div className="notice">
              Uses a merge field it does not declare in <span className="mono">variables:</span>{' '}
              — harmless today, and the declaration is what a reader checks against:{' '}
              <span className="mono">{summary.undeclared.join(', ')}</span>
            </div>
          )}
        </div>

        <div className="panel-body">
          <div className="facet">
            <span className="facet-label">Slot</span>
            <div className="chip-row">
              <button className={`chip any${slot === '' ? ' on' : ''}`} onClick={() => setSlot('')}>
                all
              </button>
              {summary.slots.map((s) => (
                <button
                  key={s}
                  className={`chip${slot === s ? ' on' : ''}`}
                  onClick={() => setSlot(slot === s ? '' : s)}
                >
                  {s} <span className="dim">{summary.by_slot[s]?.total ?? 0}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="facet">
            <span className="facet-label">Status</span>
            <div className="chip-row">
              <button
                className={`chip any${status === '' ? ' on' : ''}`}
                onClick={() => setStatus('')}
              >
                all
              </button>
              {['approved', 'pending', 'draft'].map((s) => (
                <button
                  key={s}
                  className={`chip${status === s ? ' on' : ''}`}
                  onClick={() => setStatus(status === s ? '' : s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="facet">
            <span className="facet-label">Segment</span>
            <div className="chip-row">
              <button
                className={`chip any${segment === '' ? ' on' : ''}`}
                onClick={() => setSegment('')}
              >
                all
              </button>
              {segments.map((g) => (
                <button
                  key={g}
                  className={`chip${segment === g ? ' on' : ''}`}
                  onClick={() => setSegment(segment === g ? '' : g)}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          <div className="row">
            <input
              placeholder="Search the sentences, ids and conditions"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ minWidth: 320 }}
            />
            {field && (
              <span className="badge running">
                uses {field}{' '}
                <button className="linkish" onClick={() => setField('')}>
                  clear
                </button>
              </span>
            )}
            {filtered && (
              <span className="hint">
                {shown.length} of {summary.total} shown
              </span>
            )}
          </div>
        </div>

        {summary.slots
          .filter((s) => (bySlot.get(s) ?? []).length > 0)
          .map((s) => (
            <SlotTable
              key={s}
              slot={s}
              fragments={bySlot.get(s) ?? []}
              open={open}
              setOpen={setOpen}
              onField={setField}
            />
          ))}

        {shown.length === 0 && <div className="empty panel-note">Nothing matches those filters.</div>}
      </div>

      <DataPanel data={data} field={field} setField={setField} />
      <SignalPanel data={data} />
      {coverage !== null && coverage.length > 0 && <CoveragePanel segments={coverage} />}
    </>
  )
}

function SlotTable({
  slot,
  fragments,
  open,
  setOpen,
  onField,
}: {
  slot: string
  fragments: Fragment[]
  open: string | null
  setOpen: (id: string | null) => void
  onField: (name: string) => void
}) {
  return (
    <>
      <div className="panel-body">
        <h3>
          {slot} <span className="count">{fragments.length}</span>
          <span className="hint caption">
            In composition order. The first whose condition matches wins, so a row here is read
            top to bottom and stops.
          </span>
        </h3>
      </div>
      <table>
        <thead>
          <tr>
            <th className="num">#</th>
            <th>Sentence</th>
            <th>When</th>
            <th>Data</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {fragments.map((f) => (
            <Rows key={f.id}>
              <tr
                className={`clickable${open === f.id ? ' selected' : ''}`}
                onClick={() => setOpen(open === f.id ? null : f.id)}
              >
                <td className="num mono dim">{f.position}</td>
                <td>
                  <div>{f.text}</div>
                  <div className="hint caption mono">
                    {f.id}
                    {f.source === 'override' && (
                      <span className="badge service" title="from copy/sections.yaml, not the inherited file">
                        {' '}override
                      </span>
                    )}
                    {f.variant && <span className="badge running"> arm: {f.variant}</span>}
                    {f.unreachable && <span className="badge bad"> never selected</span>}
                  </div>
                </td>
                <td style={{ maxWidth: 260 }}>
                  {f.unconditional ? (
                    <span className="dim">anybody — the last resort</span>
                  ) : (
                    <span className="mono" style={{ fontSize: 12 }}>
                      {f.when}
                    </span>
                  )}
                </td>
                <td>
                  {f.uses.length === 0 ? (
                    <span className="dim">—</span>
                  ) : (
                    <span className="chip-row">
                      {f.uses.map((u) => (
                        <button
                          key={u}
                          className="chip"
                          title={f.meanings[u] ?? 'no dictionary entry for this field'}
                          onClick={(e) => {
                            e.stopPropagation()
                            onField(u)
                          }}
                        >
                          {u}
                        </button>
                      ))}
                    </span>
                  )}
                </td>
                <td>
                  <span className={`badge ${STATUS_BADGE[f.status] ?? 'catchall'}`}>
                    {f.status}
                  </span>
                </td>
              </tr>
              {open === f.id && (
                <tr>
                  <td />
                  <td colSpan={4}>
                    {f.uses.length > 0 && (
                      <p className="hint">
                        Skipped for any account whose {f.uses.join(' / ')} is empty — that
                        account drops to the next matching sentence in this slot.
                      </p>
                    )}
                    {f.note ? (
                      <pre className="notes">{f.note}</pre>
                    ) : (
                      <p className="hint">
                        No note. Inherited fragments carry one only where v1 wrote one; every
                        override has to.
                      </p>
                    )}
                  </td>
                </tr>
              )}
            </Rows>
          ))}
        </tbody>
      </table>
    </>
  )
}

function DataPanel({
  data,
  field,
  setField,
}: {
  data: LibraryPayload
  field: string
  setField: (name: string) => void
}) {
  return (
    <div className="panel">
      <h2>
        Data the sentences draw on <span className="count">{data.fields.length}</span>
      </h2>
      <div className="panel-body">
        <p className="hint">
          Merge fields: values dropped into a chosen sentence. The meanings are the same lines
          the refine prompt is handed, so the model and the operator read one glossary. A field
          used by nothing is not a fault — it is copy nobody has written yet.
        </p>
      </div>
      <table>
        <thead>
          <tr>
            <th>Field</th>
            <th>What it is</th>
            <th className="num">Used by</th>
            <th>Slots</th>
          </tr>
        </thead>
        <tbody>
          {data.fields.map((f) => (
            <tr
              key={f.name}
              className={`clickable${field === f.name ? ' selected' : ''}`}
              onClick={() => setField(field === f.name ? '' : f.name)}
            >
              <td className="mono">
                {f.name}
                {f.unknown && <span className="badge bad"> not a real field</span>}
                {!f.in_dictionary && !f.unknown && (
                  <span className="badge parked" title="No line in dictionary.yaml">
                    {' '}
                    undocumented
                  </span>
                )}
              </td>
              <td style={{ maxWidth: 460 }}>{f.meaning ?? <span className="dim">—</span>}</td>
              <td className="num mono">
                {f.uses}
                {f.uses > 0 && f.composing_uses !== f.uses && (
                  <span className="dim"> ({f.composing_uses} live)</span>
                )}
              </td>
              <td className="dim">{f.slots.join(', ') || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function SignalPanel({ data }: { data: LibraryPayload }) {
  return (
    <div className="panel">
      <h2>
        Signals the conditions test <span className="count">{data.signals.length}</span>
      </h2>
      <div className="panel-body">
        <p className="hint">
          Not the same list as the fields above, and the difference is the point: a signal
          CHOOSES the sentence, a merge field is dropped into the one already chosen.
          `traffic_band` picks the fragment; `visits_30d` appears inside it.
        </p>
      </div>
      <table>
        <thead>
          <tr>
            <th>Signal</th>
            <th>What it is</th>
            <th>Values</th>
            <th className="num">Conditions</th>
          </tr>
        </thead>
        <tbody>
          {data.signals.map((s) => (
            <tr key={s.name}>
              <td className="mono">
                {s.name}
                {!s.known_to_engine && (
                  <span className="badge bad" title="derive() never produces this">
                    {' '}
                    unknown
                  </span>
                )}
              </td>
              <td style={{ maxWidth: 420 }}>{s.meaning ?? <span className="dim">—</span>}</td>
              <td className="dim" style={{ maxWidth: 260 }}>
                {s.values.join(' · ') || (s.kind === 'flag' ? 'yes / no' : '—')}
              </td>
              <td className="num mono">{s.uses}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CoveragePanel({ segments }: { segments: SegmentCoverage[] }) {
  return (
    <div className="panel">
      <h2>
        Copy per segment <span className="count">{segments.length}</span>
      </h2>
      <div className="panel-body">
        <p className="hint">
          Composition never fails — every slot ends in a fallback — so a segment with no copy of
          its own does not error. It composes sentences written for anybody, which is the one
          outcome that looks fine in a list and is worth nobody's inbox.
        </p>
      </div>
      <table>
        <thead>
          <tr>
            <th>Segment</th>
            <th className="num">Slots with its own copy</th>
            <th className="num">Waiting on a read</th>
            <th>Verdict</th>
          </tr>
        </thead>
        <tbody>
          {segments.map((s) => (
            <tr key={s.filter}>
              <td>
                <span className="mono">{s.group}</span>{' '}
                <span className="hint caption">{s.title}</span>
                {s.parked && <span className="badge parked"> parked</span>}
              </td>
              <td className="num mono">
                <span className={s.slots_with_own_copy === 0 ? 'dim' : ''}>
                  {s.slots_with_own_copy} / {s.slots_total}
                </span>
              </td>
              <td className="num mono">{s.awaiting_approval || <span className="dim">—</span>}</td>
              <td style={{ maxWidth: 420 }}>
                <span className="hint">{s.verdict}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
